import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { BasePage } from 'src/app/core/base-page';
import { AppSettings } from 'src/app/core/configs/app.settings';
import { ServiceHelper } from 'src/app/core/helper/services.helper';
import { Utilities } from 'src/app/core/helper/util';
import { ISocialLoginRequest, RegisterRequest } from '../../data/schema/auth-models';
import { AuthenticationService } from '../../data/service/authentication.service';
import { AnalyticsService } from 'src/app/data/service/analytics.service';
import { Log } from 'src/app/core/log';

declare const FB: any;
declare const window: any;
declare const google: any;

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
})
export class LoginFormComponent extends BasePage implements OnInit {

  // Page outputs
  @Output() loginComplete = new EventEmitter();

  // these emitters pass up the entered email so we can pre-populate the forms user is taken to
  @Output() registerClicked = new EventEmitter<string>();
  @Output() forgotClicked = new EventEmitter<string>();

  showPwd: boolean = false;
  googleBtnId: string = 'google_btn';
  model: RegisterRequest = new RegisterRequest();
  value: number = 0;

  constructor(authService: AuthenticationService, private analyticsSvc: AnalyticsService) {

    super(authService);

    // generate a unique id for the google button
    this.googleBtnId = 'google_btn_' + Utilities.getRandomNumber(3);
  }

  get version() { return AppSettings.version; }

  ngOnInit() {

    Log.debug('login complete');
    if (this.authService?.token != null) {
      this.pageState.busy = true;
      this.completeLogin(true);
      setTimeout(() => {
        this.pageState.busy = false;
      }, 3000);
    }

    this.enableSocialSDKs();

    if (localStorage.getItem('afafdawceth54') != null) {
      this.model.username = localStorage.getItem('afafdawceth54') as string;
      this.model.password = localStorage.getItem('jhr5trg34fgwe') as string;
    }
  }

  async login() {
    if (navigator.onLine === false) {
      ServiceHelper.displayAlert('No Internet Connection!', 'You are not connected to the internet. Can\'t login!');
    } else if (String.isNullOrWhiteSpace(this.model.username)) {
      ServiceHelper.displayAlert('Missing Field', 'Missing Username');
    } else if (String.isNullOrWhiteSpace(this.model.password)) {
      ServiceHelper.displayAlert('Missing Field', 'Missing Password');
    } else if (!Utilities.isValidEmail(this.model.username || '')) {
      ServiceHelper.displayAlert('Invalid Email!', 'Please enter a valid email address!');
    } else {
      try {
        this.pageState.busy = true;
        const profile = await this.authService?.login(this.model);
        this.completeLogin();
      } catch (error: any) {
        this.pageState.busy = false;
        if (error.indexOf('register') >= 0) {
          ServiceHelper.displayAlert('New here?', 'Looks like you are registering a new account, please fill out the registration form.');
          this.registerClicked.emit(this.model.username);
        } else {
          ServiceHelper.displayAlert('Login Error', error);
        }
      }
    }
  }

  register() {
    this.registerClicked.emit(this.model.username);
  }

  goToForgot() {
    this.forgotClicked.emit(this.model.username);
  }

  private async completeLogin(autoLogin: boolean = false) {
    this.loginComplete.emit(this.authService?.profile);
  }

  async facebookLogin() {
    //Google Tag Manager
    this.analyticsSvc.logFlowEvent('Facebook login');

    // Pre-Login checks
    if (navigator.onLine === false) {
      ServiceHelper.displayAlert('No Internet Connection!', 'You are not connected to the internet. Can\'t login!');
      // } 
      // else if (this.platform.is('capacitor') === true) {
      //   try {
      //     const result = await FacebookLogin.login({ permissions: ['email'] }) as FacebookLoginResponse;
      //     this.submitSocialLogin({ provider: 'facebook', token: result.accessToken.token });
      //   } catch (error) {
      //     console.error('Facebook login error.', error);
      //   }
    } else {
      // Show the FB Login
      FB.login(response => {
        Log.debug('Facebook login response:', response);
        if (response.authResponse) {
          this.submitSocialLogin({ provider: 'facebook', token: response.authResponse.accessToken });
        } else {
          console.error('Facebook login error.');
        }
      }, { scope: 'email' });
    }
  }

  async submitGoogleLogin(response: any) {
    Log.debug('google login response: ', response)

    const responsePayload = this.parseJwt(response.credential);

    Log.debug("ID: " + responsePayload.sub);
    Log.debug('Full Name: ' + responsePayload.name);
    Log.debug('Given Name: ' + responsePayload.given_name);
    Log.debug('Family Name: ' + responsePayload.family_name);
    Log.debug("Image URL: " + responsePayload.picture);
    Log.debug("Email: " + responsePayload.email);

    const profile = new RegisterRequest();
    profile.username = responsePayload.email;
    profile.lastName = responsePayload.family_name || '';
    profile.firstName = responsePayload.given_name || '';
    profile.displayName = responsePayload.given_name || responsePayload.family_name || 'anonymous';
    profile.imgUrl = responsePayload.picture;
    profile.password = 'googlesignin';

    await this.submitSocialLogin({ provider: 'google', token: response.credential, profile: profile });
  }

  private async submitSocialLogin(details: ISocialLoginRequest) {
    try {
      this.pageState.busy = true;
      await ServiceHelper.authSvc.socialLogin(details);
      this.completeLogin();
    } catch (error) {
      this.pageState.busy = false;
      console.error('Error social login', error);
      ServiceHelper.displayAlert('Login Error', error);
    }
  }

  private async enableSocialSDKs() {

    google.accounts.id.initialize({
      client_id: '408325710978-c7rb043f7eitt4u2tb09kno9lm2u6n3i.apps.googleusercontent.com',
      callback: (r) => this.submitGoogleLogin(r)
    });

    // attempt to locate the google button div for 5 seconds
    var parent = document.getElementById(this.googleBtnId);
    let attempts = 0;
    while (parent == null && attempts < 10) {
      await Utilities.wait(500);
      parent = document.getElementById(this.googleBtnId);
      Log.debug('looking for google button div... ', parent)
      attempts++;
    }

    // if we found the div, render the google button, otherwise log an error
    if (parent != null) {
      // Check if a button already exists and remove it
      if (parent.firstChild) {
        parent.removeChild(parent.firstChild);
      }

      google.accounts.id.renderButton(parent, { theme: "filled_blue", shape: "pill", size: "large" });
    } else {
      Log.debug('google button div not found after 5 seconds');
    }

    this.enableFacebookSDK();
  }

  private parseJwt(token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(jsonPayload);
  }

  private enableFacebookSDK() {
    // Add the facebook SDK To the page
    ((d, s, id) => {
      let js: any = d.getElementsByTagName(s)[0];
      const fjs = js;
      if (d.getElementById(id)) { return; }
      js = d.createElement(s); js.id = id;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode.insertBefore(js, fjs);
    })(document, 'script', 'facebook-jssdk');
    // Init the fbAsync function
    window.fbAsyncInit = () => {
      FB.init({
        appId: '282439099245657',// '1946689202259990',
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v5.0'
      });
    };
  }
}
